import { useState, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'react-toastify'
import to from 'await-to-js'
import { useNavigate, useLocation, useParams } from 'react-router-dom'
import styled from 'styled-components/macro'
import { ChevronLeft } from 'lucide-react'
import { Checkbox } from 'components/FormUtils/Checkbox'
import { TextInput } from 'components/FormUtils/TextInput'
import { TextareaInput } from 'components/FormUtils/TextareaInput'
import { PhoneInput } from 'components/FormUtils/PhoneInput'
import Select from 'components/FormUtils/Select'
import { CompanyAutocomplete } from 'components/CompanyAutocomplete'
import apiService from 'services/api'
import { BackButton } from 'components/Mobile/Mobile.styles'
import { usePreferences } from 'context/preferences/PreferencesContext'
import { ContactCompanyType } from 'models/contact_companies'
import { getIdWithPrefix } from 'utils/pathUtils'

// Same schema as desktop SuggestionModal - supports both chain and door
const suggestionFormShape = z
  .object({
    // Common fields
    incorrect_name: z.boolean().default(false),
    suggested_name: z.string().optional().nullable(),
    missing_information: z.boolean().default(false),
    something_else: z.string().optional().nullable(),

    // Chain-only fields
    incorrect_website: z.boolean().default(false),
    suggested_website: z.string().optional().nullable(),
    incorrect_category: z.boolean().default(false),
    suggested_sub_category: z.number().optional().nullable(),
    missing_doors: z.boolean().default(false),
    incorrect_doors: z.boolean().default(false),

    // Door-only fields
    incorrect_address: z.boolean().default(false),
    suggested_address: z.string().optional().nullable(),
    incorrect_phone_number: z.boolean().default(false),
    suggested_phone_number: z.string().optional().nullable(),
    location_closed: z.boolean().default(false),
    incorrect_chain_association: z.boolean().default(false),
    incorrect_chain_association_different_chain: z.boolean().default(false),
    suggested_contact_company: z
      .object({
        name: z.string(),
        id: z.number(),
        domain: z.string().nullable().optional(),
      })
      .optional()
      .nullable(),
  })
  .superRefine((data, ctx) => {
    if (data.incorrect_name && !data.suggested_name) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Suggested name is required',
        path: ['suggested_name'],
      })
    }
    if (data.incorrect_website && !data.suggested_website) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Suggested website is required',
        path: ['suggested_website'],
      })
    }
    if (data.incorrect_category && !data.suggested_sub_category) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Suggested category is required',
        path: ['suggested_sub_category'],
      })
    }
    if (data.incorrect_address && !data.suggested_address) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Suggested address is required',
        path: ['suggested_address'],
      })
    }
    if (data.incorrect_phone_number && !data.suggested_phone_number) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Suggested phone number is required',
        path: ['suggested_phone_number'],
      })
    }
  })

type SuggestionFormData = z.infer<typeof suggestionFormShape>

export function MobileSuggestionPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const urlParams = useParams()
  const { preferences } = usePreferences()
  const api = apiService()
  const { data: categories } = api.useGetCompanyCategories()
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Get data from location state
  const stateData = location.state as {
    chainProxyId?: number
    chainName?: string
    doorProxyId?: number
    doorName?: string
    doorAddress?: string
  } | null

  // Check if we're on a door page (door/:door_id path)
  const doorId = urlParams?.door_id
  const isDoorPage = !!doorId || !!stateData?.doorProxyId

  // Fetch door data if we have a door_id
  const { data: doorData } = api.useGetPlaceV2(doorId)
  const { data: doorProxyData } = api.useGetDoorProxy(
    doorData?.id ? doorData.id : undefined
  )

  // Parse chain ID from URL if not in state
  const chainID = urlParams?.chain_name
    ? getIdWithPrefix(
        urlParams?.chain_name,
        preferences?.company_type_prefix_map[ContactCompanyType.Chain]
      )
    : undefined

  // Fetch chain data if we only have chainID
  const { data: chainData } = api.useGetChain(chainID)

  // Determine the contact company ID to use
  const chainProxyId =
    doorProxyData?.chain_proxy ||
    stateData?.chainProxyId ||
    chainData?.contact_company_id

  // For door pages, show both chain name and door name
  const chainName =
    stateData?.chainName ||
    chainData?.chain ||
    doorData?.chain_proxy?.chain?.chain

  const doorName = stateData?.doorName || doorData?.restaurant?.restaurant_name

  // Build full address for door
  const doorAddress =
    stateData?.doorAddress ||
    [
      doorData?.restaurant?.address,
      doorData?.restaurant?.city,
      doorData?.restaurant?.state,
    ]
      .filter(Boolean)
      .join(', ')

  const defaultFormValues: SuggestionFormData = {
    // Common
    incorrect_name: false,
    suggested_name: null,
    missing_information: false,
    something_else: '',
    // Chain-only
    incorrect_website: false,
    suggested_website: null,
    incorrect_category: false,
    suggested_sub_category: null,
    missing_doors: false,
    incorrect_doors: false,
    // Door-only
    incorrect_address: false,
    suggested_address: null,
    incorrect_phone_number: false,
    suggested_phone_number: null,
    location_closed: false,
    incorrect_chain_association: false,
    incorrect_chain_association_different_chain: false,
    suggested_contact_company: null,
  }

  const { control, handleSubmit, formState, watch } =
    useForm<SuggestionFormData>({
      resolver: zodResolver(suggestionFormShape),
      defaultValues: defaultFormValues,
    })

  // Common watchers
  const watchIncorrectName = watch('incorrect_name')
  // Chain-only watchers
  const watchIncorrectCategory = watch('incorrect_category')
  const watchIncorrectWebsite = watch('incorrect_website')
  // Door-only watchers
  const watchIncorrectAddress = watch('incorrect_address')
  const watchIncorrectPhoneNumber = watch('incorrect_phone_number')
  const watchIncorrectChainAssociationDiffChain = watch(
    'incorrect_chain_association_different_chain'
  )

  const typeOptions = useMemo(() => {
    return (
      categories?.flatMap((category) =>
        category.sub_categories.map((subCategory) => ({
          label:
            subCategory.name === 'Other'
              ? 'Other ' + category.name
              : subCategory.name,
          value: subCategory.id,
        }))
      ) || []
    )
  }, [categories])

  const getOnlyChangedOrNewValues = (values: SuggestionFormData) => {
    return Object.keys(values).reduce(
      (acc, key) => {
        const typedKey = key as keyof SuggestionFormData
        if (values[typedKey]) {
          acc[typedKey] = values[typedKey]
        }
        return acc
      },
      {} as Record<string, unknown>
    )
  }

  const onSubmit = handleSubmit(async (data) => {
    if (!chainProxyId) {
      toast.error('Unable to submit: company not found')
      return
    }
    setIsSubmitting(true)
    const [err] = await to(
      api.postSuggestion({
        contact_company: chainProxyId,
        form_json: JSON.stringify(getOnlyChangedOrNewValues(data)),
        suggested_contact_company: null as unknown as number,
      })
    )
    setIsSubmitting(false)
    if (err) {
      toast.error(`Error submitting suggestion: ${err.message}`)
      return
    }
    toast.success('Suggestion submitted successfully!')
    navigate(-1)
  })

  return (
    <PageContainer>
      <BackButton onClick={() => navigate(-1)}>
        <ChevronLeft size={20} />
        <span>Back</span>
      </BackButton>

      <Header className="bg-gray-50">
        <Title>Suggest an edit</Title>
        {isDoorPage ? (
          <CompanyName>
            {chainName}
            {chainName && doorName && ' · '}
            {doorName}
            {doorAddress && ` - ${doorAddress}`}
          </CompanyName>
        ) : (
          chainName && <CompanyName>{chainName}</CompanyName>
        )}
      </Header>

      <FormContainer>
        <Description>
          Is something missing, or look incorrect? Let us know:
        </Description>
        {isDoorPage ? (
          <>
            {/* Door-specific fields */}
            <Checkbox control={control} name="incorrect_name">
              The name is incorrect
            </Checkbox>
            {watchIncorrectName && (
              <TextInput
                name="suggested_name"
                placeholder="Name"
                control={control}
                noMargin
              />
            )}

            <Checkbox control={control} name="incorrect_address">
              The address is incorrect
            </Checkbox>
            {watchIncorrectAddress && (
              <TextInput
                name="suggested_address"
                placeholder="123 Main St, New York, NY 10001"
                control={control}
                noMargin
              />
            )}

            <Checkbox control={control} name="incorrect_phone_number">
              The phone number is incorrect
            </Checkbox>
            {watchIncorrectPhoneNumber && (
              <PhoneInput name="suggested_phone_number" control={control} />
            )}

            <Checkbox control={control} name="location_closed">
              This location is closed
            </Checkbox>

            <Checkbox control={control} name="incorrect_chain_association">
              This location is part of a chain it shouldn&apos;t be{' '}
              <span className="text-sm text-gray-500">(split/remove)</span>
            </Checkbox>

            <Checkbox
              control={control}
              name="incorrect_chain_association_different_chain"
            >
              This location should be part of a different chain{' '}
              <span className="text-sm text-gray-500">(add)</span>
            </Checkbox>
            {watchIncorrectChainAssociationDiffChain && (
              <CompanyAutocomplete
                control={control}
                name="suggested_contact_company"
                placeholder="Start typing company name"
                label={null}
                className="!mt-0"
                chainsOnly={true}
              />
            )}

            <Checkbox control={control} name="missing_information">
              This location is missing information (add below)
            </Checkbox>
          </>
        ) : (
          <>
            {/* Chain-specific fields */}
            <Checkbox control={control} name="incorrect_category">
              This isn&apos;t a restaurant/bar
            </Checkbox>
            {watchIncorrectCategory && (
              <Select
                className="w-full !mt-1"
                control={control}
                name="suggested_sub_category"
                placeholder="Type"
                options={typeOptions}
              />
            )}

            <Checkbox control={control} name="incorrect_name">
              The name is incorrect
            </Checkbox>
            {watchIncorrectName && (
              <TextInput
                name="suggested_name"
                placeholder="Name"
                control={control}
                noMargin
              />
            )}

            <Checkbox control={control} name="incorrect_website">
              The website/domain is incorrect
            </Checkbox>
            {watchIncorrectWebsite && (
              <TextInput
                name="suggested_website"
                placeholder="website.com"
                control={control}
                noMargin
              />
            )}

            <Checkbox control={control} name="missing_doors">
              There are doors missing in this chain (add)
            </Checkbox>

            <Checkbox control={control} name="incorrect_doors">
              There are doors in this chain that shouldn&apos;t be (remove)
            </Checkbox>

            <Checkbox control={control} name="missing_information">
              There is missing data (add below)
            </Checkbox>
          </>
        )}

        <TextareaInput
          name="something_else"
          label="Something else:"
          placeholder="Share details about what should be added or changed."
          control={control}
        />
      </FormContainer>

      <Footer>
        <CancelButton onClick={() => navigate(-1)} disabled={isSubmitting}>
          Cancel
        </CancelButton>
        <SubmitButton
          onClick={onSubmit}
          disabled={!formState.isValid || isSubmitting}
        >
          {isSubmitting ? 'Submitting...' : 'Submit'}
        </SubmitButton>
      </Footer>
    </PageContainer>
  )
}

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: white;
`

const Header = styled.div`
  padding: 16px;
  border-bottom: 1px solid #e5e7eb;
`

const Title = styled.h1`
  font-size: 20px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 4px 0;
`

const CompanyName = styled.p`
  font-size: 14px;
  color: #047857;
`

const Description = styled.p`
  font-size: 14px;
  margin: 0;
`

const FormContainer = styled.div`
  flex: 1;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-bottom: 180px;
`

const Footer = styled.div`
  position: fixed;
  bottom: 60px;
  left: 0;
  right: 0;
  display: flex;
  gap: 12px;
  padding: 16px;
  background: #f9fafb;
  border-top: 1px solid #e5e7eb;
`

const CancelButton = styled.button`
  flex: 1;
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: white;
  color: #374151;
  font-weight: 500;
  font-size: 16px;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
  }
`

const SubmitButton = styled.button`
  flex: 1;
  padding: 12px 16px;
  border: none;
  border-radius: 8px;
  background: #065f46;
  color: white;
  font-weight: 500;
  font-size: 16px;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
  }
`
